import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { get, isNil, omitBy, pick } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import Grid from '~/components/core/Atomic/Grid/Grid';
import CONFIGURABLE_LOBS from '~/server_shared/generated-types/CONFIGURABLE_LOBS';

import { cleanEmptyValues } from '../../../../../Utils';
import { useIncidentConfiguration } from '../../../../hooks/useIncidentConfiguration';
import { cleanConfiguredFieldsFormikValuesBeforeSend } from '../../../../IncidentConfiguration/ConfiguredFields';
import { involvedGenericFields } from '../InvolvedGenericParty/InvolvedGenericFragment';

import GeneralInvolvedPartiesCard from './GeneralInvolvedPartiesCard';

const FIRST_PARTY_VALUES = 'first_party_values';
const THIRD_PARTY_VALUES = 'third_party_values';

const getGeneralInvolvedPartiesValidationSchema = () => {
  return {
    [FIRST_PARTY_VALUES]: Yup.array()
      .of(
        Yup.object().shape({
          involved_details_filled: Yup.boolean().oneOf([true], 'Required'),
        })
      )
      .min(1, 'At least one first party is required'),
    // Gate on the invariant "an unreviewed party has no name": `handleAddParty` seeds every
    // manually added party with `involved_details_filled: false` too, so this is not a
    // carried-party-only predicate — it is inert for manual parties only because their seed leaves
    // the name fields empty until the dialog save flips the flag to true. A party that has a name
    // while still flagged unreviewed was carried over by Create Like, and needs an explicit
    // decision — review and save it, or remove it — rather than being dropped at submit.
    [THIRD_PARTY_VALUES]: Yup.array().test(
      'carried-third-parties-reviewed',
      'Review or remove the third parties carried over from the original claim',
      (parties) =>
        (parties || []).every(
          (party) => !getInvolvedGenericNameIfExist(party) || party.involved_details_filled !== false
        )
    ),
  };
};

const generalInvolvedPartiesInitialValues = {
  first_party_values: [],
  third_party_values: [],
};

const compactValues = (obj) => omitBy(obj || {}, isNil);

// Carried (create-like) parties contain only the fields the source claim had, while the dialog
// inputs expect the explicit empty values a manually-added party is seeded with (e.g. '' for
// selects). Merge each carried party over its party type's blank seed so both creation paths
// produce identically-shaped party objects.
//
// This covers the first render only: `handleSaveParty` runs the saved values back through
// `cleanConfiguredFieldsFormikValuesBeforeSend` without `shouldSendNullForEmpty`, which omits empty
// keys outright, so reopening the dialog hands those selects `undefined` again. That is symmetric
// with the manual-add path rather than a carried-party-specific gap, so it is left as-is here.
const seedCarriedInvolvedParties = (incidentConfiguration, parties) =>
  (parties || []).map((party) => {
    const seed = involvedGenericFields(incidentConfiguration, party.general_claim_involved_type);
    return {
      ...seed,
      ...compactValues(party),
      configured_fields_values: { ...seed.configured_fields_values, ...compactValues(party.configured_fields_values) },
      related_property_loss: { ...seed.related_property_loss, ...compactValues(party.related_property_loss) },
      property_damage: { ...seed.property_damage, ...compactValues(party.property_damage) },
      bodily_injury_loss: { ...seed.bodily_injury_loss, ...compactValues(party.bodily_injury_loss) },
      pecuniary_loss: { ...seed.pecuniary_loss, ...compactValues(party.pecuniary_loss) },
      covered_object_id: party.covered_object_id ?? '',
      covered_object_external_id: party.covered_object_external_id ?? '',
    };
  });

const externalIdGetOrGenerate = (obj) => obj?.external_id ?? uuidv4();

const buildGeneralInvolvedParties = ({ values }) => {
  const involvedParties = [];

  values?.first_party_values.forEach((party) => {
    involvedParties.push({
      ...cleanEmptyValues(party),
      external_id: externalIdGetOrGenerate(party),
      type: party.type,
      is_first_party: true,
      property_damage: cleanEmptyValues(get(party, 'property_damage', {})),
      related_property_loss: cleanEmptyValues(get(party, 'related_property_loss', {})),
      bodily_injury_loss: cleanEmptyValues(get(party, 'bodily_injury_loss', {})),
      pecuniary_loss: cleanEmptyValues(get(party, 'pecuniary_loss', {})),
    });
  });

  values?.third_party_values.forEach((party) => {
    // Same predicate as the `carried-third-parties-reviewed` schema test above: a party that has a
    // name but is still flagged unreviewed was carried by Create Like and must not be submitted.
    // Manual parties also seed `involved_details_filled: false`, so the flag alone does not
    // identify a carried party — it is the name that does.
    if (getInvolvedGenericNameIfExist(party) && party.involved_details_filled !== false) {
      involvedParties.push({
        ...cleanEmptyValues(party),
        external_id: externalIdGetOrGenerate(party),
        type: party.type,
        contact_id: party.contact_id,
        bodily_injuries: party?.injuries || [],
        is_first_party: false,
        property_damage: cleanEmptyValues(get(party, 'property_damage', {})),
        related_property_loss: cleanEmptyValues(get(party, 'related_property_loss', {})),
        bodily_injury_loss: cleanEmptyValues(get(party, 'bodily_injury_loss', {})),
        pecuniary_loss: cleanEmptyValues(get(party, 'pecuniary_loss', {})),
      });
    }
  });
  return involvedParties;
};

const adjustGeneralInvolvedProperty = (genericPropertyInvolved) => {
  return {
    ...genericPropertyInvolved,
    property_damage: {
      ...pick(genericPropertyInvolved, CONFIGURABLE_LOBS.LOSS_GROUP_FIELDS.property_damage),
      property_damages: genericPropertyInvolved?.damages || [],
      property_damages_description: genericPropertyInvolved?.damages_description,
      is_property_stolen: genericPropertyInvolved?.is_stolen,
    },
    related_property_loss: {
      ...genericPropertyInvolved?.related_property_loss,
      damages: genericPropertyInvolved?.related_property_loss?.damages || [],
    },
    pecuniary_loss: {
      ...genericPropertyInvolved?.pecuniary_loss,
    },
  };
};

const adjustInvolvedPerson = (involvedPerson) => {
  return {
    ...involvedPerson,
    bodily_injury_loss: {
      ...pick(involvedPerson, CONFIGURABLE_LOBS.LOSS_GROUP_FIELDS.bodily_injury_loss),
      bodily_injuries: involvedPerson.injuries || [],
    },
    related_property_loss: {
      ...involvedPerson?.related_property_loss,
      damages: involvedPerson?.related_property_loss?.damages || [],
    },
    pecuniary_loss: {
      ...involvedPerson?.pecuniary_loss,
    },
  };
};

const GeneralInvolvedPartiesCardFormik = () => {
  const { setFieldValue, values } = useFormikContext();
  const firstPartyValues = values[FIRST_PARTY_VALUES];
  const thirdPartyValues = values[THIRD_PARTY_VALUES];
  const { incidentConfiguration } = useIncidentConfiguration();

  const incidentConfigParties = useMemo(
    () => incidentConfiguration?.involved_parties || {},
    [incidentConfiguration?.involved_parties]
  );

  const handleAddParty = useCallback(
    (partyTypeKey) => {
      const partyIncidentConfig = incidentConfigParties[partyTypeKey];
      const isFirstParty = partyIncidentConfig?.is_first_party;
      const newValues = [
        ...(isFirstParty ? firstPartyValues : thirdPartyValues),
        {
          ...involvedGenericFields(incidentConfiguration, partyTypeKey),
          id: uuidv4(),
          type: partyTypeKey,
          general_claim_involved_type: partyTypeKey,
          covered_object_id: '',
          covered_object_external_id: '',
          involved_details_filled: false,
        },
      ];
      setFieldValue(isFirstParty ? FIRST_PARTY_VALUES : THIRD_PARTY_VALUES, newValues);
    },
    [firstPartyValues, thirdPartyValues, incidentConfiguration, incidentConfigParties, setFieldValue]
  );

  // Initialize first party if none exist
  useEffect(() => {
    if (firstPartyValues.length === 0 && Object.keys(incidentConfigParties).length > 0) {
      const incidentConfigFirstPartyKeys = Object.keys(incidentConfigParties).filter(
        (key) => incidentConfigParties[key]?.active && incidentConfigParties[key]?.is_first_party
      );
      // Only auto-initialize if there's exactly one first party type
      // If multiple types exist, let user choose which to add
      if (incidentConfigFirstPartyKeys.length === 1) {
        handleAddParty(incidentConfigFirstPartyKeys[0]);
      }
    }
  }, [firstPartyValues.length, incidentConfigParties, handleAddParty]);

  const handleSaveParty = async (partyValues, partyTypeKey) => {
    const partyIncidentConfig = incidentConfigParties[partyTypeKey];
    const isFirstParty = partyIncidentConfig?.is_first_party;
    const configuredFields = get(incidentConfiguration, `involved_parties.${partyTypeKey}.configured_fields`, []);
    const cleanConfiguredFields = cleanConfiguredFieldsFormikValuesBeforeSend(
      configuredFields,
      get(partyValues, 'configured_fields_values')
    );
    const fieldArrayName = isFirstParty ? FIRST_PARTY_VALUES : THIRD_PARTY_VALUES;
    partyValues = { ...partyValues, involved_details_filled: true, configured_fields_values: cleanConfiguredFields };
    const curIdx = values[fieldArrayName].findIndex((party) => party.id === partyValues.id);
    await Promise.resolve(setFieldValue(`${fieldArrayName}[${curIdx}]`, partyValues));
  };

  const handleDeleteFirstParty = useCallback(
    async (partyValues) => {
      // A claim must always keep at least one first party, so the last one can
      // never be deleted regardless of how many first party types are configured.
      if (firstPartyValues.length <= 1) {
        return;
      }

      const newFirstPartyValues = values.first_party_values.filter((party, _) => party.id !== partyValues.id);
      await Promise.resolve(setFieldValue(FIRST_PARTY_VALUES, newFirstPartyValues));
    },
    [values.first_party_values, firstPartyValues.length, setFieldValue]
  );

  const handleDeleteThirdParty = async (partyValues) => {
    const newThirdPartyValues = values.third_party_values.filter((party, _) => party.id !== partyValues.id);
    await Promise.resolve(setFieldValue(THIRD_PARTY_VALUES, newThirdPartyValues));
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <GeneralInvolvedPartiesCard
          firstPartyValues={firstPartyValues}
          thirdPartyValues={thirdPartyValues}
          onFetchInvolvedDetails={(involvedHeader) => involvedHeader}
          onAddParty={handleAddParty}
          onSaveParty={handleSaveParty}
          onDeleteFirstParty={handleDeleteFirstParty}
          onDeleteThirdParty={handleDeleteThirdParty}
          isFnol
        />
      </Grid>
    </Grid>
  );
};

const getInvolvedGenericNameIfExist = (involvedGeneric) => {
  const contactFullName = involvedGeneric?.contact_full_name ?? involvedGeneric?.owner_contact_full_name;
  const displayName =
    involvedGeneric?.display_name ?? involvedGeneric.property_name ?? involvedGeneric.property_description;
  return displayName || contactFullName;
};

export {
  adjustGeneralInvolvedProperty,
  adjustInvolvedPerson,
  buildGeneralInvolvedParties,
  FIRST_PARTY_VALUES,
  GeneralInvolvedPartiesCardFormik,
  generalInvolvedPartiesInitialValues,
  getGeneralInvolvedPartiesValidationSchema,
  getInvolvedGenericNameIfExist,
  seedCarriedInvolvedParties,
};
